/**
 * @napplet/sdk -- Relay, inter-napplet pubsub, and storage wrapper objects.
 *
 * @packageDocumentation
 */

import type {
  NostrEvent,
  NostrFilter,
  NappletGlobal,
  IncEvent,
  RelayEventResult,
  Subscription,
  EventTemplate,
} from '@napplet/core';
import { requireDomain } from './require-napplet.js';

type SdkDomain<K extends keyof NappletGlobal> = NonNullable<NappletGlobal[K]>;

/**
 * NIP-01 relay operations: subscribe to events, publish events, one-shot queries.
 *
 * Each method delegates to `window.napplet.relay.*` at call time, which in turn
 * routes through the shell's relay pool via postMessage.
 *
 * @example
 * ```ts
 * import { relay } from '@napplet/sdk';
 *
 * const sub = relay.subscribe(
 *   { kinds: [1], limit: 10 },
 *   (event) => console.log(event),
 *   () => console.log('EOSE'),
 * );
 * ```
 */
export const relay: SdkDomain<'relay'> = {
  /**
   * Open a live NIP-01 subscription through the shell's relay pool.
   * @param filters  One or more NIP-01 subscription filters
   * @param onEvent  Called for each matching event result
   * @param onEose   Called when the shell signals end of stored events (EOSE)
   * @param options  Optional: `{ relay, group }` for NIP-29 scoped relay subscriptions
   * @returns A Subscription handle with a `close()` method
   */
  subscribe(
    filters: NostrFilter | NostrFilter[],
    onEvent: (result: RelayEventResult) => void,
    onEose: () => void,
    options?: { relay?: string; group?: string },
  ): Subscription {
    return requireDomain('relay').subscribe(filters, onEvent, onEose, options);
  },

  /**
   * Sign and publish a Nostr event through the shell.
   * @param template  Unsigned event template
   * @param options   Optional: `{ relay: true }` to publish via scoped relay
   * @returns The signed NostrEvent after successful publication
   */
  publish(
    template: EventTemplate,
    options?: { relay?: boolean },
  ): Promise<NostrEvent> {
    return requireDomain('relay').publish(template, options);
  },

  /**
   * Publish an encrypted Nostr event through the shell.
   * @param template    Unsigned event template
   * @param recipient   Hex-encoded recipient public key
   * @param encryption  Encryption scheme: 'nip44' (default) or 'nip04'
   * @returns The signed encrypted NostrEvent after successful publication
   */
  publishEncrypted(
    template: EventTemplate,
    recipient: string,
    encryption: 'nip44' | 'nip04' = 'nip44',
  ): Promise<NostrEvent> {
    return requireDomain('relay').publishEncrypted(template, recipient, encryption);
  },

  /**
   * One-shot query: subscribe, collect events until EOSE, then resolve.
   * @param filters  NIP-01 subscription filters
   * @returns Promise resolving to array of matching event results
   */
  query(filters: NostrFilter | NostrFilter[]): Promise<RelayEventResult[]> {
    return requireDomain('relay').query(filters);
  },
};

/**
 * Inter-napplet pubsub: broadcast and receive INC events through the shell.
 *
 * @example
 * ```ts
 * import { inc } from '@napplet/sdk';
 *
 * inc.emit('napplet:profile/open', { pubkey: '...' });
 *
 * const sub = inc.on('napplet:profile/open', (event) => {
 *   console.log('Profile requested:', event.payload);
 * });
 * ```
 */
export const inc: SdkDomain<'inc'> = {
  /**
   * Broadcast an INC message to other napplets via the shell.
   * @param topic    An opaque stable topic or convention URI
   * @param payload  Optional opaque payload for a queryless topic
   */
  emit(topic: string, payload?: unknown): void {
    requireDomain('inc').emit(topic, payload);
  },

  /**
   * Subscribe to INC events on a specific topic.
   * @param topic     Exact topic value to listen for
   * @param callback  Called with one runtime-attested INC event
   * @returns A Subscription handle with a `close()` method
   */
  on(
    topic: string,
    callback: (event: IncEvent) => void,
  ): Subscription {
    return requireDomain('inc').on(topic, callback);
  },

  channel: {
    open(target) {
      return requireDomain('inc').channel.open(target);
    },
    onOpened(callback) {
      return requireDomain('inc').channel.onOpened(callback);
    },
    list() {
      return requireDomain('inc').channel.list();
    },
    broadcast(payload) {
      requireDomain('inc').channel.broadcast(payload);
    },
  },
};

/**
 * @deprecated Use {@link inc}. NAP-IFC was renamed to NAP-INC; this package
 * alias is kept only to ease downstream migration.
 */
export const ifc: SdkDomain<'inc'> = inc;

/**
 * Napplet-scoped storage: async localStorage-like API proxied through the shell.
 * Each napplet's storage is isolated by identity.
 *
 * @example
 * ```ts
 * import { storage } from '@napplet/sdk';
 *
 * await storage.setItem('theme', 'dark');
 * const theme = await storage.getItem('theme'); // 'dark'
 * ```
 */
export const storage: SdkDomain<'storage'> = {
  /**
   * Retrieve a stored value by key. Returns null if the key does not exist.
   * @param key  The storage key
   * @returns The stored string value, or null if not found
   */
  getItem(key: string): Promise<string | null> {
    return requireDomain('storage').getItem(key);
  },

  /**
   * Store a key-value pair.
   * @param key    The storage key
   * @param value  The string value to store
   */
  setItem(key: string, value: string): Promise<void> {
    return requireDomain('storage').setItem(key, value);
  },

  /**
   * Remove a stored key.
   * @param key  The storage key to remove
   */
  removeItem(key: string): Promise<void> {
    return requireDomain('storage').removeItem(key);
  },

  /**
   * List all keys stored by this napplet.
   * @returns Array of storage key strings
   */
  keys(): Promise<string[]> {
    return requireDomain('storage').keys();
  },

  /**
   * Per-instance storage: same surface as the shared methods, but scoped to this
   * napplet instance (sets `scope: "instance"` on the wire) rather than shared
   * across instances. Defer to NAP-STORAGE (napplet/naps) for scope semantics.
   */
  instance: {
    /**
     * Retrieve a per-instance value by key. Returns null if not found.
     * @param key  The storage key
     */
    getItem(key: string): Promise<string | null> {
      return requireDomain('storage').instance.getItem(key);
    },

    /**
     * Store a per-instance key-value pair.
     * @param key    The storage key
     * @param value  The string value to store
     */
    setItem(key: string, value: string): Promise<void> {
      return requireDomain('storage').instance.setItem(key, value);
    },

    /**
     * Remove a per-instance key.
     * @param key  The storage key to remove
     */
    removeItem(key: string): Promise<void> {
      return requireDomain('storage').instance.removeItem(key);
    },

    /**
     * List all per-instance keys for this napplet instance.
     * @returns Array of storage key strings
     */
    keys(): Promise<string[]> {
      return requireDomain('storage').instance.keys();
    },
  },
};
